import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { InterviewClient } from "@/components/interview/interview-client";
import { LocaleProvider } from "@/lib/i18n/context";
import { getDictionary } from "@/lib/i18n/dictionaries";
import { dirFor, isLocale, type Locale, DEFAULT_LOCALE } from "@/lib/i18n/config";

type InterviewBootstrap =
  | { state: "invalid" }
  | { state: "completed"; language?: string }
  | {
      state: "ready";
      language?: string;
      candidateName?: string;
      jobTitle?: string | null;
      companyName?: string | null;
      questionCount?: number;
      maxDurationSec?: number;
    };

export function CandidateInterviewPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<InterviewBootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    fetch(`/api/interview/${token}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<InterviewBootstrap>;
      })
      .then((json) => {
        if (active) setData(json);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 to-background text-muted dark:from-brand-950/60">
        Loading…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 to-background text-muted dark:from-brand-950/60">
        {error || "Failed to load"}
      </div>
    );
  }

  if (data.state === "invalid") {
    const t = getDictionary(DEFAULT_LOCALE);
    return (
      <Message
        locale={DEFAULT_LOCALE}
        icon={<XCircle className="h-12 w-12 text-red-500" />}
        title={t.invalid.invalidTitle}
        body={t.invalid.invalidBody}
      />
    );
  }

  const locale: Locale =
    data.language && isLocale(data.language) ? data.language : DEFAULT_LOCALE;
  const t = getDictionary(locale);

  if (data.state === "completed") {
    return (
      <Message
        locale={locale}
        icon={<CheckCircle2 className="h-12 w-12 text-emerald-500" />}
        title={t.invalid.completedTitle}
        body={t.invalid.completedBody}
      />
    );
  }

  return (
    <LocaleProvider locale={locale}>
      <div dir={dirFor(locale)}>
        <InterviewClient
          token={token!}
          candidateName={data.candidateName ?? "there"}
          jobTitle={data.jobTitle ?? null}
          companyName={data.companyName ?? null}
          questionCount={data.questionCount}
          maxDurationSec={data.maxDurationSec}
        />
      </div>
    </LocaleProvider>
  );
}

function Message({
  locale,
  icon,
  title,
  body,
}: {
  locale: Locale;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      dir={dirFor(locale)}
      className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 to-background px-4 dark:from-brand-950/60"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        <div className="mx-auto mb-4 flex justify-center">{icon}</div>
        <h1 className="text-xl font-bold">{title}</h1>
        <p className="mt-2 text-muted">{body}</p>
        <p className="mt-6 flex items-center justify-center gap-1 text-xs text-muted">
          <Clock className="h-3 w-3" /> {getDictionary(locale).common.appName}
        </p>
      </div>
    </div>
  );
}
